import { ComboBox, IComboBox, IComboBoxOption } from "@fluentui/react";
import React, { FunctionComponent, useCallback, useEffect, useState } from "react";
import { localize } from "src/l10n";
import api from "src/spintr/SpintrApi";
import { SpintrTypes } from "src/typings";
import { Loader } from "src/ui";
import { FormControl } from "src/ui/components/Forms";

interface IProps {
    selectedId?: string;
    onChange(id: string): void;
}

const CalendarSelector: FunctionComponent<IProps> = ({ selectedId, onChange }) => {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState<Spintr.ICalendar[]>([]);

    const onComboBoxChange = useCallback(
        (_: React.FormEvent<IComboBox>, option: IComboBoxOption) => {
            onChange(option.key.toString());
        },
        [onChange]
    );

    useEffect(() => {
        api
            .get<Spintr.ICalendar[]>(
                "/api/v1/Calendar", {
                    params: {
                        // Every department can have its own calendar, so a large
                        // customer passes 100 easily. The endpoint sorts by name and
                        // the ComboBox filters as you type, so one generous page is
                        // enough until this needs proper server-side search.
                        take: 1000,
                    },
                },
            )
            .then(
                (response) => {
                    setLoading(false);

                    // Only central calendars can be picked - a calendar tied to a
                    // department is not meant to be put on the start page.
                    //
                    // The endpoint also hands administrators deleted, unpublished and
                    // draft calendars, since the calendar admin list needs to show
                    // them. None of those can ever produce events for a puff.
                    setItems(() => (response.data || []).filter((calendar) =>
                        calendar.type === SpintrTypes.CalendarType.Public &&
                        !calendar.isDeleted &&
                        !calendar.isDraft &&
                        calendar.isPublished
                    ));
                },
                () => {
                    setLoading(false);
                }
            )
    }, [setItems, setLoading]);

    if (loading) {
        return <Loader />;
    }

    return (
        <FormControl>
            <ComboBox
                allowFreeform={false}
                autoComplete={"on"}
                label={localize("appCalendars")}
                onChange={onComboBoxChange}
                options={
                    items.map((calendar) => ({
                        key: calendar.id.toString(),
                        text: calendar.name,
                    }))
                }
                selectedKey={selectedId}
            />
        </FormControl>
    )
};

export default CalendarSelector;
